import { Check, Edit2, Loader2, X } from 'lucide-react';
import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { useSalesPotentialEditor } from 'src/features/novelties/hooks/useSalesPotentialEditor';
import { cn } from 'src/lib/utils';

type SalesPotentialEditorProps = {
  sku: string;
  salesPotential: number | null;
};

export function SalesPotentialEditor({
  sku,
  salesPotential,
}: SalesPotentialEditorProps): ReactElement {
  const { t } = useTranslation('novelties');
  const {
    isEditing,
    editValue,
    isPending,
    setEditValue,
    handleEdit,
    handleCancel,
    handleSave,
    handleInputClick,
    handleKeyDown,
  } = useSalesPotentialEditor({ sku, salesPotential });

  return (
    <div
      className='flex flex-col items-end gap-1'
      data-sales-potential-editor
      role='none'
      onMouseDown={(e) => e.stopPropagation()}
    >
      <span className='text-xs text-layout-foreground-secondary'>
        {t('Sales potential')}
      </span>
      {isEditing ? (
        <div
          className='flex items-center gap-2'
          role='button'
          onClick={handleInputClick}
          tabIndex={0}
          onKeyDown={handleKeyDown}
        >
          <Input
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className='h-8 w-24 text-right text-sm'
            placeholder='0'
            autoFocus
            min={0}
          />
          <Button
            size='icon'
            variant='ghost'
            className='size-8'
            onClick={handleSave}
            disabled={isPending}
          >
            {isPending ? (
              <Loader2 className='size-4 animate-spin' />
            ) : (
              <Check className='size-4 text-success-default' />
            )}
          </Button>
          <Button
            size='icon'
            variant='ghost'
            className='size-8'
            onClick={handleCancel}
            disabled={isPending}
          >
            <X className='size-4 text-error-default' />
          </Button>
        </div>
      ) : (
        <div className='flex items-center gap-2'>
          <span
            className={cn(
              'font-semibold',
              salesPotential === null
                ? 'text-layout-foreground-secondary'
                : 'text-primary-default',
            )}
          >
            {salesPotential !== null ? salesPotential.toLocaleString() : '-'}
          </span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size='icon'
                  variant='ghost'
                  className='size-8'
                  onClick={handleEdit}
                >
                  <Edit2 className='size-4' />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t('Edit sales potential')}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}
    </div>
  );
}
